import {useCallback, useMemo} from 'react';
import {call, fork, select} from 'redux-saga/effects';
import {useSelector} from "react-redux";

import {useToast} from "@chakra-ui/react";
import {useStoreCommunityList} from "../store/hooks";
import {actions, useIsomorphicSagaEffect, useSagaCallback, useSagaEffect} from '../store/sagas';
import {selectCommunities, selectLoggedIn, selectProfile, selectStaleCommunities} from "../store/selectors";
import {apiGet, apiPost, apiPostForm, useGetRequestData, useSagaResult} from './client';

export function* loadCommunities() {
    const {communities} = yield call(apiGet, `/api/v2/community/communities.json`);
    yield actions.communityListLoaded('communities', communities, Date.now() + 2 * 60 * 1000);
}

export const useReloadCommunities = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* () {
        const {communities} = yield call(apiGet, `/api/v2/community/communities.json`);
        return yield actions.communityListLoaded('communities', communities, Date.now() + 2 * 60 * 1000);
    }, []))
};

export const useCommunityList = (feedbackCh, sort = 'communities', pinned = null) => {
    const isDefault = sort === 'featured';
    const isSubscribed = sort === 'subscribed';
    const subscribed = useSelector((state) => selectProfile(state).subscribed || []);
    const moderates = useSelector((state) => selectProfile(state).moderates || []);
    const communities = useStoreCommunityList('communities');
    return useMemo(() => {
        const pred = (community) => {
            if (pinned && pinned.includes(community.name)) return true;
            if (isDefault) return community.is_default;
            if (isSubscribed) return subscribed.includes(community.name);
            return true;
        };
        return communities?.filter(pred).map((community) => ({
            ...community,
            is_moderator: moderates.includes(community.name),
            is_subscribed: subscribed.includes(community.name),
        }));
    }, [communities, isDefault, isSubscribed, subscribed, moderates, pinned]);
};

export const useCommunitySubscribe = (feedbackCh) => {
    const toast = useToast();
    return useSagaCallback(feedbackCh, useCallback(function* (names) {
        const loggedIn = yield select(selectLoggedIn);
        const {subscribed} = yield select(selectProfile);
        const newNames = [];
        const oldNames = [];
        for (const name of names) {
            const index = subscribed.indexOf(name);
            const wasSubscribed = index !== -1;
            (wasSubscribed ? oldNames : newNames).push(name);
            toast({
                title: `You ${wasSubscribed ? 'unsubscribed from' : 'subscribed to'} c/${name}!`,
                status: 'success',
                isClosable: true,
            })
        }
        yield actions.settingsChanged({
            subscribed: [
                ...subscribed.filter(element => !oldNames.includes(element)),
                ...newNames
            ]
        });
        yield fork(function* () {
            if (loggedIn) {
                for (const name of newNames) {
                    yield call(apiPost, `/api/v2/community/subscribe`, {community: name});
                }
                for (const name of oldNames) {
                    yield call(apiPost, `/api/v2/community/unsubscribe`, {community: name});
                }
            }
            yield actions.feedsCleared('home');
        })
    }, [toast]));
};

export const useModeratorList = (feedbackCh, community) =>
    useGetRequestData(feedbackCh, community && `/api/v2/community/moderators.json?community=${community}`)?.moderators;

export const useCommunityExists = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community) {
        const {communities} = yield call(apiGet, `/api/v2/community/exists.json?community=${community}`);
        return communities[0].exists;
    }, []))
};

export function* getCommunityStyle(community) {
    if (!community || community.toLowerCase() === 'win') {
        return null;
    }
    const data = yield call(apiGet, `/api/v2/community/style.json?community=${community}`);
    return data.communities[0];
}

export const useCommunityStyle = (community) =>
    useSagaResult(null, useCallback(() => getCommunityStyle(community), [community]));

export const useCommunityStylesheet = (feedbackCh, community, setCode) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/community/stylesheet.json?community=${community}`;
        const data = yield call(apiGet, url);
        setCode(data.communities[0].stylesheet);
    }, [community, setCode]));
};

export const useCommunitySettings = (feedbackCh, community, setSettings) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/community/settings.json?community=${community}`;
        const data = yield call(apiGet, url);
        const settings = data.communities[0];
        setSettings({
            displayName: settings.displayName,
            tagline: settings.tagline,
            description: settings.description,
            metaTags: settings.metaTags,
            minScorePosts: settings.minScorePosts,
            minScoreComments: settings.minScoreComments,
            minScoreDownPosts: settings.minScoreDownPosts,
            minScoreDownComments: settings.minScoreDownComments,
            visibility: settings.visibility,
            defaultPostType: settings.defaultPostType,
            defaultCommentSort: settings.defaultCommentSort,
            allowTextPosts: settings.allowTextPosts,
            allowImagePosts: settings.allowImagePosts,
            allowLinkPosts: settings.allowLinkPosts,
            contentFilters: settings.contentFilters,
            allowPostEditing: settings.allowPostEditing,
            allowCommentEditing: settings.allowCommentEditing,
            allowComments: settings.allowComments,
            allowChildComments: settings.allowChildComments,
            publicLogs: settings.publicLogs,
            obsfucatedLogs: settings.obsfucatedLogs,
            notifyReportConclusion: settings.notifyReportConclusion,
            topics: settings.topics,
            banLogs: settings.banLogs,
            appSafe: settings.appSafe,
        });
    }, [community]));
};

export const useCommunityFilter = (feedbackCh, community, setCode) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/community/filter.json?community=${community}`;
        const data = yield call(apiGet, url);
        setCode(data.communities[0].filter);
    }, [community, setCode]));
};

export const useCommunityResources = (feedbackCh, community, setResources) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/community/resources.json?community=${community}`;
        const data = yield call(apiGet, url);
        setResources(data.resources);
    }, [community, setResources]));
};

export const useCommunitySidebar = (feedbackCh, community, setCode) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/community/sidebar.json?community=${community}`;
        const data = yield call(apiGet, url);
        setCode(data.communities[0].sidebar);
    }, [community, setCode]));
};

export const useSaveSidebar = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community, sidebar) {
        return yield call(apiPost, `/api/v2/community/set_sidebar.json`, {
            community,
            sidebar
        });
    }, []))
};

export const useSaveStylesheet = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community, stylesheet) {
        const data = {community};
        if (stylesheet != null) data.stylesheet = stylesheet;
        return yield call(apiPost, `/api/v2/community/set_stylesheet.json`, data);
    }, []))
};

export const useSaveFilter = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community, filter) {
        return yield call(apiPost, `/api/v2/community/set_filter.json`, {
            community,
            filter
        });
    }, []))
};

export const useSaveMenu = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community, menu) {
        return yield call(apiPost, `/api/v2/community/set_menu.json`, {
            community,
            menu
        });
    }, []))
};

export const useSaveSettings = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community, settings) {
        return yield call(apiPostForm, `/api/v2/community/settings.json`, {
            community,
            displayName: settings.displayName,
            tagline: settings.tagline,
            description: settings.description,
            metaTags: settings.metaTags,
            minScorePosts: settings.minScorePosts,
            minScoreComments: settings.minScoreComments,
            minScoreDownPosts: settings.minScoreDownPosts,
            minScoreDownComments: settings.minScoreDownComments,
            visibility: settings.visibility,
            defaultPostType: settings.defaultPostType,
            defaultCommentSort: settings.defaultCommentSort,
            allowTextPosts: settings.allowTextPosts,
            allowImagePosts: settings.allowImagePosts,
            allowLinkPosts: settings.allowLinkPosts,
            contentFilters: settings.contentFilters,
            allowPostEditing: settings.allowPostEditing,
            allowCommentEditing: settings.allowCommentEditing,
            allowComments: settings.allowComments,
            allowChildComments: settings.allowChildComments,
            publicLogs: settings.publicLogs,
            obsfucatedLogs: settings.obsfucatedLogs,
            notifyReportConclusion: settings.notifyReportConclusion,
            topics: JSON.stringify(settings.topics),
            banLogs: settings.banLogs,
            appSafe: settings.appSafe,
        });
    }, []))
};

export const useReserveCommunity = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (name, displayName, description, apolitical, notes) {
        return yield call(apiPost, `/api/v2/action/community.json`, {
            name,
            displayName,
            description,
            apolitical: !apolitical,
            notes
        });
    }, []))
};

export const useCreateCommunity = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (name, displayName, description, politics, nsfw) {
        return yield call(apiPost, `/api/v2/action/create_community.json`, {
            name,
            displayName,
            description,
            politics: !politics,
            nsfw: !nsfw
        });
    }, []))
};

export const useLogs = (feedbackCh, community, page, type, moderator, logType) =>
    useGetRequestData(feedbackCh, community && `/api/v2/community/${logType === 'all' ? 'logs' : 'ban-logs'}.json?community=${community}${page ? `&page=${page}` : ''}${type ? `&type=${type}` : ''}${moderator ? `&moderator=${moderator}` : ''}`)?.logs;

/**
 * useCommunities returns the full list of communities requested by names.
 * Communities that are not already loaded will show up as null and will be requested from API.
 * Communities that were loaded more than 2 minutes ago will be refreshed from API.
 * @param feedbackCh
 * @param names {string[]}
 * @returns {object[]}
 */
export const useCommunities = (feedbackCh, names) => {
    useIsomorphicSagaEffect(feedbackCh, useCallback(function* () {
        const staleNames = yield select(selectStaleCommunities(names, Date.now()));
        if (staleNames.length > 0) {
            const url = '/api/v2/community/community.json';
            const { communities } = yield call(apiGet, url, { communities: staleNames.join(',') });
            yield actions.communitiesLoaded(communities, Date.now() + 2 * 60 * 1000);
        }
    }, [names.join(',')]));
    const subscribed = useSelector((state) => selectProfile(state).subscribed || []);
    const moderates = useSelector((state) => selectProfile(state).moderates || []);
    const communities = useSelector(selectCommunities(names));
    return useMemo(() => {
        return communities?.map((community) => community && ({
            ...community,
            is_moderator: moderates.includes(community.name),
            is_subscribed: subscribed.includes(community.name),
        }));
    }, [communities, subscribed, moderates]);
};

export const useReloadCommunity = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (names) {
                const url = '/api/v2/community/community.json';
                const { communities } = yield call(apiGet, url, { communities: names.join(',') });
                yield actions.communitiesLoaded(communities, Date.now() + 2 * 60 * 1000);
    }, []))
};

export function* getAllCommunities(sort, topic, page, showPolitics) {
    const {communities} = yield call(apiGet, `/api/v2/community/communities.json?sort=${sort}&topic=${topic}&page=${page}&showPolitics=${showPolitics}`);
    yield actions.communitiesLoaded(communities, Date.now() + 2 * 60 * 1000);
    return communities;
}

export const useAllCommunities = (feedbackCh, sort, topic, page, showPolitics) => {
    const communities = useSagaResult(null, useCallback(() => getAllCommunities(sort, topic, page, showPolitics), [sort, topic, page, showPolitics]));
    const subscribed = useSelector((state) => selectProfile(state).subscribed || []);
    const moderates = useSelector((state) => selectProfile(state).moderates || []);
    return useMemo(() => {
        return communities?.map((community) => community && ({
            ...community,
            is_moderator: moderates.includes(community.name),
            is_subscribed: subscribed.includes(community.name),
        }));
    }, [communities, subscribed, moderates]);
};